import React, { useState, useEffect } from 'react';
import { 
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiX, FiCheck,
  FiEye, FiRefreshCw, FiExternalLink, FiPlusCircle, FiCalendar, FiClock,
  FiUser, FiShield, FiActivity, FiEye as FiEyeDiag, FiBriefcase
} from 'react-icons/fi';
import { patientService, inventoryService } from '../../services/api';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Inventory States
  const [inventoryFrames, setInventoryFrames] = useState([]);
  const [inventoryLenses, setInventoryLenses] = useState([]);
  const [inventoryAccessories, setInventoryAccessories] = useState([]);
  const [showManualFrame, setShowManualFrame] = useState(false);
  const [showManualLens, setShowManualLens] = useState(false);

  // Modal States
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [viewingHistoryRecord, setViewingHistoryRecord] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Edit Form State
  const [formData, setFormData] = useState(null);
  const [isNewVisit, setIsNewVisit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPatients();
    fetchInventory();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const data = await patientService.getAll();
      setPatients(data);
    } catch (error) {
      toast.error('Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const inv = await inventoryService.getAll();
      setInventoryFrames(inv.filter(i => i.category === 'frame'));
      setInventoryLenses(inv.filter(i => i.category === 'lens'));
      setInventoryAccessories(inv.filter(i => i.category === 'accessory'));
    } catch (e) {
      console.error('Failed to load inventory', e);
    }
  };

  const handleDelete = async (id) => {
    try {
      await patientService.delete(id);
      toast.success('Patient record deleted successfully');
      setDeleteConfirm(null);
      fetchPatients();
    } catch (error) {
      toast.error('Failed to delete patient');
    }
  };

  const parseJsonField = (field) => {
    if (!field) return {};
    if (typeof field === 'object') return field;
    try {
      return JSON.parse(field) || {};
    } catch (e) {
      return {};
    }
  };

  const handleEditOpen = (patient) => {
    setFormData({
      ...patient,
      family_history: patient.family_history || '',
      first_wear: patient.first_wear || 'no',
      ocular_movement: patient.ocular_movement || '',
      convergence: patient.convergence || '',
      color_vision: patient.color_vision || '',
      ophthalmoscopy: patient.ophthalmoscopy || '',
      slit_lamp: patient.slit_lamp || '',
      visual_acuity: parseJsonField(patient.visual_acuity),
      habitual_rx: parseJsonField(patient.habitual_rx),
      auto_refraction: parseJsonField(patient.auto_refraction),
      retinoscopy: parseJsonField(patient.retinoscopy),
      subjective: parseJsonField(patient.subjective),
      dispensing_details: parseJsonField(patient.dispensing_details)
    });
    setIsNewVisit(false);
    setEditModal(true);
  };

  const handleNewVisitOpen = (patient) => {
    setFormData({
      ...patient,
      // Clinical Fields are RESET for new visit
      family_history: patient.family_history || '',
      first_wear: 'no',
      ocular_movement: '',
      convergence: '',
      color_vision: '',
      ophthalmoscopy: '',
      slit_lamp: '',
      visual_acuity: {},
      habitual_rx: {},
      auto_refraction: {},
      retinoscopy: {},
      subjective: {},
      dispensing_details: {
        pd: '',
        segment_height: '',
        frame_material: 'plastic',
        frame_demographic: 'unisex',
        lens_type: 'none',
        reading_glasses: 'none',
        remarks: 'Fresh exam.'
      }
    });
    setIsNewVisit(true);
    setEditModal(true);
  };

  const handleEditSave = async () => {
    try {
      setSaving(true);
      if (isNewVisit) {
        const payload = { ...formData, patient_id: formData.id };
        await patientService.addHistory(payload);
        toast.success('New visit record added successfully');
      } else {
        await patientService.update(formData);
        toast.success('Patient record updated successfully');
      }
      setEditModal(false);
      fetchPatients();
    } catch (error) {
      toast.error(isNewVisit ? 'Failed to log new visit' : 'Failed to update patient');
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetails = async (patient) => {
    setSelectedPatient(patient);
    setDetailModal(true);
    try {
      const history = await patientService.getHistory(patient.id);
      setPatientHistory(history);
      if (history.length > 0) {
        const latest = {
          ...history[0],
          visual_acuity: parseJsonField(history[0].visual_acuity),
          habitual_rx: parseJsonField(history[0].habitual_rx),
          auto_refraction: parseJsonField(history[0].auto_refraction),
          retinoscopy: parseJsonField(history[0].retinoscopy),
          subjective: parseJsonField(history[0].subjective),
          dispensing_details: parseJsonField(history[0].dispensing_details)
        };
        setViewingHistoryRecord(latest);
      }
    } catch (e) {
      toast.error('Failed to load visit history');
    }
  };

  const selectHistoryRecord = (record) => {
    setViewingHistoryRecord({
      ...record,
      visual_acuity: parseJsonField(record.visual_acuity),
      habitual_rx: parseJsonField(record.habitual_rx),
      auto_refraction: parseJsonField(record.auto_refraction),
      retinoscopy: parseJsonField(record.retinoscopy),
      subjective: parseJsonField(record.subjective),
      dispensing_details: parseJsonField(record.dispensing_details)
    });
  };

  const handleDeleteHistory = async (historyId, patientId) => {
    if (!window.confirm('Are you sure you want to permanently delete this specific visit record? This action cannot be undone.')) return;
    
    try {
      await patientService.deleteHistory(historyId, patientId);
      toast.success('Visit record deleted successfully');
      
      // Refresh local state
      const updatedHistory = patientHistory.filter(h => h.id !== historyId);
      setPatientHistory(updatedHistory);
      
      if (updatedHistory.length > 0) {
        selectHistoryRecord(updatedHistory[0]);
      } else {
        setViewingHistoryRecord(null);
      }
      
      fetchPatients(); // Refetch to update main table latest record
    } catch (error) {
      toast.error('Failed to delete visit record');
    }
  };

  const filteredPatients = patients.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (p.contact && p.contact.includes(searchQuery)) ||
    (p.reg_no && p.reg_no.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const updateClinicalField = (section, eye, field, value) => {
    setFormData(prev => {
      const updatedSection = { ...prev[section] };
      updatedSection[eye] = { ...updatedSection[eye], [field]: value };
      return { ...prev, [section]: updatedSection };
    });
  };

  const handleDispensingChange = (e) => {
    const { name, value } = e.target;
    if (name === 'frame_brand') {
      const sf = inventoryFrames.find(f => f.item_name === value);
      if (sf && sf.details) {
        const d = typeof sf.details === 'string' ? JSON.parse(sf.details) : sf.details;
        setFormData(prev => ({
          ...prev,
          dispensing_details: { ...prev.dispensing_details, [name]: value, frame_material: d.material || 'plastic', frame_demographic: d.demographic || 'unisex', frame_metal_type: d.metal_frame_type || 'full_frame' }
        }));
        return;
      }
    }
    if (name === 'accessory_id') {
      const acc = inventoryAccessories.find(a => a.id.toString() === value);
      setFormData(prev => ({ ...prev, dispensing_details: { ...prev.dispensing_details, [name]: value, accessory_name: acc ? acc.item_name : '' } }));
      return;
    }
    setFormData(prev => ({ ...prev, dispensing_details: { ...prev.dispensing_details, [name]: value } }));
  };

  const toggleManualFrame = () => {
    setShowManualFrame(!showManualFrame);
    setFormData(prev => ({ ...prev, dispensing_details: { ...prev.dispensing_details, frame_brand: '' } }));
  };

  const toggleManualLens = () => {
    setShowManualLens(!showManualLens);
    setFormData(prev => ({ ...prev, dispensing_details: { ...prev.dispensing_details, lens_type: 'none' } }));
  };

  const renderVAInputRow = (eye) => {
    return (
      <tr key={eye} className="border-b border-slate-50 last:border-none">
        <td className="p-3 text-xs font-black text-slate-800 bg-slate-50 border-r border-slate-100">{eye}</td>
        {['unaided', 'ph', 'aided'].map(type => (
          <td key={type} className="p-2 border-r border-slate-50 last:border-none">
            <div className="flex gap-1 items-center">
               <input type="text" placeholder="Num" value={formData.visual_acuity[eye]?.[type]?.num || ''} onChange={e => { const val = { ...formData.visual_acuity[eye]?.[type], num: e.target.value }; updateClinicalField('visual_acuity', eye, type, val); }} className="w-full min-w-[35px] px-1 py-1 text-[10px] font-bold bg-white border border-slate-200 rounded text-center" />
               <span className="text-slate-300">/</span>
               <input type="text" placeholder="Den" value={formData.visual_acuity[eye]?.[type]?.den || ''} onChange={e => { const val = { ...formData.visual_acuity[eye]?.[type], den: e.target.value }; updateClinicalField('visual_acuity', eye, type, val); }} className="w-full min-w-[35px] px-1 py-1 text-[10px] font-bold bg-white border border-slate-200 rounded text-center" />
               <select value={formData.visual_acuity[eye]?.[type]?.sign || 'none'} onChange={e => { const val = { ...formData.visual_acuity[eye]?.[type], sign: e.target.value }; updateClinicalField('visual_acuity', eye, type, val); }} className="text-[9px] bg-slate-50 rounded"><option value="none">-</option><option value="+">+</option><option value="-">-</option></select>
            </div>
          </td>
        ))}
      </tr>
    );
  };

  const renderClinicalInputRow = (section, eye) => {
    const fields = ['sph', 'cyl', 'axis', 'dva', 'ph', 'add', 'nva'];
    return (
      <tr key={`${section}-${eye}`} className="border-b border-slate-50 last:border-none">
        <td className="p-3 text-xs font-black text-slate-800 bg-slate-50 border-r border-slate-100">{eye}</td>
        {fields.map(field => (
          <td key={field} className="p-2 border-r border-slate-50 last:border-none">
            <input type="text" value={formData[section][eye]?.[field] || ''} onChange={e => updateClinicalField(section, eye, field, e.target.value)} className="w-full px-1.5 py-1.5 text-[11px] font-bold bg-white border border-slate-200 rounded text-center focus:border-indigo-400 outline-none transition-colors" placeholder="-" />
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className="space-y-8 animate-fade-in-down pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-3xl border border-slate-100 shadow-sm gap-4">
        <div className="relative w-full max-w-lg">
          <FiSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search full registry..." className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-slate-900/5 text-sm font-semibold transition-all shadow-inner" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <button onClick={() => window.open('/admin/patients/new', '_blank')} className="w-full md:w-auto px-10 py-4 bg-slate-900 text-white rounded-2xl flex items-center justify-center space-x-3 shadow-xl hover:bg-slate-800 transition-all text-xs font-black uppercase tracking-widest"><FiPlus /><span>New Registration</span></button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50">
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Registry</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Identity</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Communication</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr><td colSpan="4" className="text-center py-24 text-slate-300 text-xs font-black uppercase tracking-[0.3em] animate-pulse">Synchronizing Data...</td></tr>
              ) : filteredPatients.length === 0 ? (
                <tr><td colSpan="4" className="text-center py-24 text-slate-300 text-[10px] font-black uppercase tracking-[0.2em]">No matching dossiers found.</td></tr>
              ) : filteredPatients.map((patient) => (
                <tr key={patient.id} className="hover:bg-slate-50/80 transition-all group">
                  <td className="px-8 py-6 truncate"><span className="px-3 py-1.5 bg-slate-100 text-slate-900 rounded-xl text-[10px] font-black tracking-widest border border-slate-200">{patient.reg_no}</span></td>
                  <td className="px-8 py-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-11 h-11 bg-slate-900 rounded-2xl flex items-center justify-center text-white font-black text-lg">{patient.name.charAt(0)}</div>
                      <div>
                         <p className="font-black text-slate-900 text-sm tracking-tight capitalize">{patient.name}</p>
                         <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{patient.age} Yrs • DOB: {patient.dob}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <div className="space-y-1">
                      <p className="text-xs font-black text-slate-600">{patient.contact}</p>
                      <p className="text-[10px] font-bold text-slate-400 truncate max-w-[200px]">{patient.address}</p>
                    </div>
                  </td>
                  <td className="px-8 py-6 text-right">
                    <div className="flex items-center justify-end space-x-2">
                       <button onClick={() => handleNewVisitOpen(patient)} className="p-2.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all" title="New Visit/Record"><FiPlusCircle size={18} /></button>
                       <button onClick={() => handleViewDetails(patient)} className="p-2.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" title="View History dossier"><FiEye size={18} /></button>
                       <button onClick={() => handleEditOpen(patient)} className="p-2.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-all" title="Edit Latest dossier"><FiEdit2 size={18} /></button>
                       <button onClick={() => setDeleteConfirm(patient)} className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all" title="Permanent Delete"><FiTrash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal (Full Clinical History Dossier) */}
      <AnimatePresence>
        {detailModal && selectedPatient && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDetailModal(false)} className="absolute inset-0 bg-slate-900/40 backdrop-blur-md"></motion.div>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative bg-white w-full max-w-7xl max-h-[92vh] rounded-xl shadow-2xl overflow-hidden flex flex-row">
               
               {/* Sidebar: Visit Timeline */}
               <div className="w-64 bg-slate-50 border-r border-slate-100 flex flex-col">
                  <div className="p-8 border-b border-slate-200">
                     <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Visit Timeline</h4>
                     <div className="space-y-3 max-h-[60vh] overflow-y-auto no-scrollbar">
                        {patientHistory.map((rec, index) => (
                           <div className="relative group/card">
                              <button 
                                key={rec.id} 
                                onClick={() => selectHistoryRecord(rec)}
                                className={`w-full p-4 rounded-2xl text-left transition-all border ${viewingHistoryRecord?.id === rec.id ? 'bg-slate-900 border-slate-900 shadow-lg translate-x-1' : 'bg-white border-slate-100 hover:bg-white hover:shadow-md'}`}
                              >
                                 <div className="flex items-center justify-between mb-1">
                                    <span className={`text-[8px] font-black uppercase ${viewingHistoryRecord?.id === rec.id ? 'text-slate-400' : 'text-slate-500'}`}>
                                       Visit #{patientHistory.length - index}
                                    </span>
                                    <FiCalendar size={10} className={viewingHistoryRecord?.id === rec.id ? 'text-teal-400' : 'text-slate-300'} />
                                 </div>
                                 <p className={`text-[11px] font-black truncate ${viewingHistoryRecord?.id === rec.id ? 'text-white' : 'text-slate-800'}`}>
                                    {new Date(rec.visit_date).toLocaleDateString()}
                                 </p>
                                 <div className="flex items-center space-x-1 mt-1 opacity-60">
                                    <FiClock size={8} className={viewingHistoryRecord?.id === rec.id ? 'text-white' : 'text-slate-400'} />
                                    <span className={`text-[8px] font-bold ${viewingHistoryRecord?.id === rec.id ? 'text-white' : 'text-slate-400'}`}>
                                       {new Date(rec.visit_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                 </div>
                              </button>
                              <button 
                                 onClick={(e) => { e.stopPropagation(); handleDeleteHistory(rec.id, selectedPatient.id); }}
                                 className="absolute -top-1 -right-1 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover/card:opacity-100 hover:bg-red-600 transition-all shadow-lg scale-90 hover:scale-100"
                                 title="Delete this visit"
                              >
                                 <FiTrash2 size={10} />
                              </button>
                           </div>
                        ))}
                     </div>
                  </div>
                  <div className="p-8 mt-auto group">
                     <button onClick={() => {setDetailModal(false); handleNewVisitOpen(selectedPatient);}} className="w-full py-4 bg-teal-50 text-teal-600 rounded-2xl text-[9px] font-black uppercase tracking-widest border border-teal-100 hover:bg-teal-600 hover:text-white transition-all flex items-center justify-center space-x-2">
                        <FiPlusCircle /><span>Add New Visit</span>
                     </button>
                  </div>
               </div>

               {/* Main Content Area */}
               <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-white">
                     <div className="flex items-center space-x-5">
                        <div className="w-16 h-16 bg-slate-900 rounded-[1.25rem] flex items-center justify-center text-white text-2xl font-black shadow-xl shadow-slate-200">{selectedPatient.name.charAt(0)}</div>
                        <div>
                           <h3 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">{selectedPatient.name}</h3>
                           <div className="flex items-center space-x-3 mt-1">
                              <span className="px-3 py-1 bg-syana-blue-50 text-syana-blue-600 text-[9px] font-black rounded-lg border border-syana-blue-100 uppercase tracking-widest">{selectedPatient.reg_no} Dossier</span>
                              {selectedPatient.first_wear === 'yes' && <span className="px-3 py-1 bg-orange-50 text-orange-600 text-[9px] font-black rounded-lg border border-orange-100 uppercase tracking-widest">Initial Wearer</span>}
                           </div>
                        </div>
                     </div>
                     <button onClick={() => setDetailModal(false)} className="p-3 bg-white border border-slate-200 hover:bg-slate-100 rounded-2xl transition-all shadow-sm"><FiX size={24} /></button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-8 space-y-12 no-scrollbar bg-[#f8fafc]/50">
                     {/* Identity Section (Common) */}
                     <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                           <div className="flex items-center space-x-3 text-indigo-600 border-b border-indigo-50 pb-2"><FiUser className="text-lg" /><h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Identity & Demographic Profile</h4></div>
                           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm"><p className="text-[9px] font-black text-slate-400 uppercase mb-2">Age / Bio-Date</p><p className="text-sm font-black text-slate-800">{selectedPatient.age} Yrs • {selectedPatient.dob}</p></div>
                              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm"><p className="text-[9px] font-black text-slate-400 uppercase mb-2">Contact Utility</p><p className="text-sm font-black text-slate-800">{selectedPatient.contact || 'None listed'}</p></div>
                              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm col-span-full"><p className="text-[9px] font-black text-slate-400 uppercase mb-2">Primary Residence</p><p className="text-sm font-bold text-slate-700 italic leading-relaxed">"{selectedPatient.address}"</p></div>
                           </div>
                        </div>
                        <div className="space-y-6">
                           <div className="flex items-center space-x-3 text-red-600 border-b border-red-50 pb-2"><FiShield className="text-lg" /><h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Medical Safeguards</h4></div>
                           <div className="bg-white p-5 rounded-2xl border-l-4 border-l-orange-500 shadow-sm"><p className="text-[9px] font-black text-slate-400 uppercase mb-2">Medical & Family History</p><p className="text-xs font-bold text-slate-800 leading-normal">Med: {selectedPatient.medical_history || 'N/A'}<br/>Fam: {selectedPatient.family_history || 'N/A'}</p></div>
                        </div>
                     </div>

                     {/* Viewing Record Details (Dynamic) */}
                     {viewingHistoryRecord ? (
                        <>
                           <div className="bg-syana-blue-600 p-6 rounded-xl text-white flex justify-between items-center shadow-lg">
                              <div className="flex items-center space-x-4">
                                 <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center"><FiActivity size={24} /></div>
                                 <div>
                                    <p className="text-[9px] font-black text-indigo-200 uppercase tracking-[0.2em]">Record under inspection</p>
                                    <h4 className="text-xl font-black">{new Date(viewingHistoryRecord.visit_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</h4>
                                 </div>
                              </div>
                              <div className="text-right">
                                 <p className="text-[9px] font-black text-indigo-200 uppercase tracking-widest">Staff Record</p>
                                 <p className="text-xs font-bold opacity-80">Verified on {new Date(viewingHistoryRecord.visit_date).toLocaleTimeString()}</p>
                              </div>
                           </div>

                           <div className="space-y-12">
                              {/* Diagnostic Summary */}
                              <div className="space-y-6">
                                 <div className="flex items-center space-x-3 text-purple-600 border-b border-purple-50 pb-2"><FiEyeDiag className="text-lg" /><h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Diagnostic Sensory Observations</h4></div>
                                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm"><p className="text-[9px] font-black text-slate-400 uppercase mb-2">Ocular Movement</p><p className="text-sm font-black text-slate-900">{viewingHistoryRecord.ocular_movement || 'N/A'}</p></div>
                                    <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm"><p className="text-[9px] font-black text-slate-400 uppercase mb-2">Convergence / NPC</p><p className="text-sm font-black text-slate-900">{viewingHistoryRecord.convergence || 'N/A'}</p></div>
                                    <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm"><p className="text-[9px] font-black text-slate-400 uppercase mb-2">Color Vision Status</p><p className="text-sm font-black text-slate-900">{viewingHistoryRecord.color_vision || 'N/A'}</p></div>
                                 </div>
                              </div>

                              {/* Tables */}
                              <div className="space-y-8">
                                 {['Visual Acuity', 'Habitual RX', 'Auto Refraction', 'Retinoscopy', 'Subjective'].map(t => {
                                    const sk = t.toLowerCase().replace(' ', '_');
                                    const data = viewingHistoryRecord[sk];
                                    if (!data || Object.keys(data).length === 0) return null;
                                    return (
                                       <div key={t} className="bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm">
                                          <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                             <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{t}</h5>
                                             <span className="text-[8px] font-black px-2 py-1 bg-white rounded border border-slate-200">Ref: {sk.slice(0,3).toUpperCase()}</span>
                                          </div>
                                          <table className="w-full text-center">
                                             <thead><tr className="bg-slate-50/20 text-[9px] font-bold text-slate-400 uppercase tracking-widest"><th className="p-4 w-20 border-r border-slate-100">Eye</th>{t === 'Visual Acuity' ? ['Unaided', 'PH', 'Aided'].map(h => <th key={h} className="p-4 border-r border-slate-100 last:border-none">{h}</th>) : ['sph', 'cyl', 'axis', 'dva', 'ph', 'add', 'nva'].map(h => <th key={h} className="p-4 border-r border-slate-100 last:border-none">{h}</th>)}</tr></thead>
                                             <tbody className="divide-y divide-slate-100 text-sm font-black text-slate-800">{['R', 'L'].map(eye => (
                                                <tr key={eye}><td className="p-4 bg-slate-50/50 text-indigo-600 border-r border-slate-100">{eye}</td>{t === 'Visual Acuity' ? ['unaided', 'ph', 'aided'].map(ty => <td key={ty} className="p-4 border-r border-slate-100 last:border-none">{data[eye]?.[ty]?.num && data[eye]?.[ty]?.den ? `${data[eye][ty].num}/${data[eye][ty].den} ${data[eye][ty].sign !== 'none' ? data[eye][ty].sign : ''}` : '-'}</td>) : ['sph', 'cyl', 'axis', 'dva', 'ph', 'add', 'nva'].map(f => <td key={f} className="p-4 border-r border-slate-100 last:border-none">{data[eye]?.[f] || '-'}</td>)}</tr>
                                             ))}</tbody>
                                          </table>
                                       </div>
                                    );
                                 })}
                              </div>

                              {/* Advanced */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                 <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4">Ophthalmoscopy Findings</p><p className="text-xs font-bold text-slate-700 leading-relaxed italic">"{viewingHistoryRecord.ophthalmoscopy || 'No findings reported.'}"</p></div>
                                 <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4">Slit Lamp Microscopy</p><p className="text-xs font-bold text-slate-700 leading-relaxed italic">"{viewingHistoryRecord.slit_lamp || 'No microscopy reported.'}"</p></div>
                              </div>

                              {/* Dispensing Section (Black Box) */}
                              <div className="bg-slate-900 rounded-xl p-10 text-white relative overflow-hidden shadow-2xl transition-transform hover:scale-[1.01]">
                                 <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative z-10">
                                    <div><p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-4">Measurements</p><div className="flex space-x-8"><div><p className="text-[8px] text-slate-500 mb-1">PD</p><p className="text-3xl font-black text-teal-400">{viewingHistoryRecord.dispensing_details.pd || '-'} mm</p></div><div><p className="text-[8px] text-slate-500 mb-1">SEG HEIGHT</p><p className="text-3xl font-black text-teal-400">{viewingHistoryRecord.dispensing_details.segment_height || '-'} mm</p></div></div></div>
                                    <div><p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-4">Frame Selection</p><p className="text-xl font-black text-white">{viewingHistoryRecord.dispensing_details.frame_brand || 'Patient Own'}</p><div className="flex gap-2 mt-2"><span className="px-2 py-1 bg-white/5 rounded text-[8px] font-black uppercase">{viewingHistoryRecord.dispensing_details.frame_material}</span><span className="px-2 py-1 bg-white/5 rounded text-[8px] font-black uppercase text-teal-400">{viewingHistoryRecord.dispensing_details.reading_glasses} Utility</span></div></div>
                                    <div><p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-4">Lens & Feedback</p><p className="text-sm font-black text-slate-300">{viewingHistoryRecord.dispensing_details.lens_type?.replace(/_/g, ' ')}</p><p className="text-[10px] text-slate-500 mt-4 leading-relaxed">"{viewingHistoryRecord.dispensing_details.remarks || 'Standard professional remark.'}"</p></div>
                                 </div>
                              </div>
                           </div>
                        </>
                     ) : (
                        <div className="flex flex-col items-center justify-center py-32 text-slate-300">
                           <FiActivity size={60} className="mb-6 opacity-20" />
                           <p className="text-xs font-black uppercase tracking-[0.3em]">Select a visit date from the timeline</p>
                        </div>
                     )}
                  </div>

                  <div className="p-8 bg-white border-t border-slate-100 flex justify-end space-x-3 shadow-top">
                     <button onClick={() => {setDetailModal(false); handleNewVisitOpen(selectedPatient);}} className="px-10 py-4 bg-teal-50 text-teal-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-teal-600 hover:text-white transition-all">New Examination</button>
                     <button onClick={() => setDetailModal(false)} className="px-14 py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">Close Portfolio</button>
                  </div>
               </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Expanded Edit Modal (Multi-Mode: Edit Latest or Add Visit) */}
      <AnimatePresence>
        {editModal && formData && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditModal(false)} className="absolute inset-0 bg-slate-900/40 backdrop-blur-md"></motion.div>
            <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="relative bg-white w-full max-w-6xl max-h-[95vh] rounded-xl shadow-2xl overflow-hidden flex flex-col">
               <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-20 shadow-sm">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-2xl ${isNewVisit ? 'bg-teal-100 text-teal-600' : 'bg-orange-100 text-orange-600'}`}>
                       {isNewVisit ? <FiPlusCircle size={24} /> : <FiEdit2 size={24} />}
                    </div>
                    <div>
                       <h3 className="text-xl font-black text-slate-900 tracking-tight uppercase">
                          {isNewVisit ? 'Add New Clinical Visit Record' : 'Correct Professional Dossier Record'}
                       </h3>
                       <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{formData.name} • {formData.reg_no}</p>
                    </div>
                  </div>
                  <button onClick={() => setEditModal(false)} className="p-2 text-slate-400 hover:text-slate-900 transition-colors"><FiX size={28} /></button>
               </div>

               <div className="p-8 overflow-y-auto space-y-12 no-scrollbar">
                  {/* Identity & Safeguards (Read Only in New Visit Mode) */}
                  <div className="space-y-6">
                     <div className="flex items-center space-x-3 text-indigo-600 border-l-4 border-indigo-600 pl-4 bg-indigo-50/30 py-1.5 rounded-r-lg">
                        <FiShield className="text-sm" /><h4 className="text-[11px] font-black uppercase tracking-[0.2em] pr-6">1. Identity & Clinical Records</h4>
                        {isNewVisit && <span className="text-[8px] font-black bg-white px-2 py-0.5 rounded border border-indigo-200">Demographic Lock active</span>}
                     </div>
                     <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Full Legal Name</label><input type="text" value={formData.name} readOnly={isNewVisit} className={`edit-input w-full ${isNewVisit ? 'opacity-50 grayscale bg-slate-100' : ''}`} /></div>
                        <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Contact No</label><input type="text" value={formData.contact} onChange={e => !isNewVisit && setFormData({...formData, contact: e.target.value})} readOnly={isNewVisit} className={`edit-input w-full ${isNewVisit ? 'opacity-50 grayscale bg-slate-100' : ''}`} /></div>
                        <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Age</label><input type="text" value={formData.age} readOnly className="edit-input w-full opacity-50 grayscale bg-slate-100" /></div>
                        <div className="space-y-1">
                           <label className="text-[10px] font-black text-slate-400 uppercase">First Time Wearer?</label>
                           <div className="flex space-x-2 mt-1">
                              {['yes', 'no'].map(v => (
                                 <button key={v} onClick={() => setFormData({...formData, first_wear: v})} className={`flex-1 py-2 text-[9px] font-black uppercase tracking-widest rounded-lg border-2 transition-all ${formData.first_wear === v ? 'bg-slate-900 border-slate-900 text-white shadow-md' : 'bg-slate-50 border-slate-100 text-slate-400 hover:bg-slate-100'}`}>{v === 'yes' ? 'Yes' : 'No'}</button>
                              ))}
                           </div>
                        </div>
                        <div className="col-span-1 md:col-span-2 space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Medical History</label><textarea value={formData.medical_history || ''} onChange={e => setFormData({...formData, medical_history: e.target.value})} className="edit-input w-full min-h-[80px]" placeholder="Previous conditions..." /></div>
                        <div className="col-span-1 md:col-span-2 space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Family Ocular History</label><textarea value={formData.family_history || ''} onChange={e => setFormData({...formData, family_history: e.target.value})} className="edit-input w-full min-h-[80px]" placeholder="History of Glaucoma, Diabetes, etc." /></div>
                     </div>
                  </div>

                  {/* Clinical Sections - Always Editable */}
                  <div className="space-y-8">
                     <div className="flex items-center space-x-3 text-syana-blue-600 border-l-4 border-syana-blue-600 pl-4 bg-syana-blue-50/30 py-1.5 rounded-r-lg"><FiActivity className="text-sm" /><h4 className="text-[11px] font-black uppercase tracking-[0.2em] pr-6">2. Clinical Performance Tables</h4></div>
                     {['Visual Acuity', 'Habitual RX', 'Auto Refraction', 'Retinoscopy', 'Subjective'].map((s) => {
                        const sk = s.toLowerCase().replace(' ', '_'); const isVA = s === 'Visual Acuity';
                        return (
                           <div key={sk} className="bg-slate-50 border border-slate-100 rounded-2xl overflow-hidden">
                              <div className="px-5 py-3 bg-slate-100/50 border-b border-slate-100"><p className="text-[10px] font-black uppercase text-slate-500">{s}</p></div>
                              <table className="w-full">
                                 <thead><tr className="bg-white/30 border-b border-slate-50"><th className="p-3 text-[9px] font-bold text-slate-400 uppercase w-16 border-r border-slate-100">Eye</th>{isVA ? ['unaided', 'ph', 'aided'].map(h => <th key={h} className="p-3 text-[9px] font-bold text-slate-400 uppercase border-r border-slate-100 last:border-none uppercase">{h}</th>) : ['sph', 'cyl', 'axis', 'dva', 'ph', 'add', 'nva'].map(h => <th key={h} className="p-3 text-[9px] font-bold text-slate-400 uppercase border-r border-slate-100 last:border-none uppercase">{h}</th>)}</tr></thead>
                                 <tbody className="bg-white">{['R', 'L'].map(eye => isVA ? renderVAInputRow(eye) : renderClinicalInputRow(sk, eye))}</tbody>
                              </table>
                           </div>
                        );
                     })}
                  </div>

                  <div className="space-y-6">
                     <div className="flex items-center space-x-3 text-purple-600 border-l-4 border-purple-600 pl-4 bg-purple-50/30 py-1.5 rounded-r-lg"><FiEyeDiag className="text-sm" /><h4 className="text-[11px] font-black uppercase tracking-[0.2em] pr-6">3. Diagnostic Observations</h4></div>
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Ocular Movement / Tropia</label><input type="text" value={formData.ocular_movement} onChange={e => setFormData({...formData, ocular_movement: e.target.value})} className="edit-input w-full" placeholder="e.g. SAFE" /></div>
                        <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Convergence</label><input type="text" value={formData.convergence} onChange={e => setFormData({...formData, convergence: e.target.value})} className="edit-input w-full" placeholder="e.g. NPC 8cm" /></div>
                        <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Color Vision</label><input type="text" value={formData.color_vision} onChange={e => setFormData({...formData, color_vision: e.target.value})} className="edit-input w-full" placeholder="e.g. Normal" /></div>
                        <div className="col-span-1 md:col-span-2 space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Ophthalmoscopy Observations</label><textarea value={formData.ophthalmoscopy} onChange={e => setFormData({...formData, ophthalmoscopy: e.target.value})} className="edit-input w-full min-h-[80px]" /></div>
                        <div className="col-span-1 space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Slit Lamp Microscopy</label><textarea value={formData.slit_lamp} onChange={e => setFormData({...formData, slit_lamp: e.target.value})} className="edit-input w-full min-h-[80px]" /></div>
                     </div>
                  </div>

                  <div className="space-y-8 pb-10">
                     <div className="flex items-center space-x-3 text-teal-600 border-l-4 border-teal-600 pl-4 bg-teal-50/30 py-1.5 rounded-r-lg"><FiBriefcase className="text-sm" /><h4 className="text-[11px] font-black uppercase tracking-[0.2em] pr-6">4. Dispensing & Fitting Specifications</h4></div>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="space-y-6">
                           <div className="flex items-center justify-between border-b border-slate-100 pb-2"><h5 className="text-[10px] font-black text-slate-400 uppercase">Frame Selection</h5><button type="button" onClick={toggleManualFrame} className={`text-[8px] font-black px-2 py-1 rounded border transition-all ${showManualFrame ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>Manual Mode</button></div>
                           <div className="space-y-4">
                              <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Brand Name</label>{showManualFrame ? <input type="text" name="frame_brand" value={formData.dispensing_details.frame_brand} onChange={handleDispensingChange} className="edit-input w-full border-indigo-200" /> : <select name="frame_brand" value={formData.dispensing_details.frame_brand} onChange={handleDispensingChange} className="edit-input w-full cursor-pointer"><option value="">Select frame...</option><option value="patient_own">Patients Own</option>{inventoryFrames.map(f => <option key={f.id} value={f.item_name}>{f.item_name}</option>)}</select>}</div>
                              <div className="grid grid-cols-2 gap-4"><div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Material</label><select name="frame_material" value={formData.dispensing_details.frame_material} onChange={handleDispensingChange} className="edit-input w-full"><option value="plastic">Plastic</option><option value="metal">Metal</option></select></div><div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase">Demographic</label><select name="frame_demographic" value={formData.dispensing_details.frame_demographic} onChange={handleDispensingChange} className="edit-input w-full"><option value="unisex">Unisex</option><option value="gents">Gents</option><option value="women">Women</option><option value="children">Children</option></select></div></div>
                           </div>
                        </div>
                        <div className="space-y-6">
                           <div className="flex items-center justify-between border-b border-slate-100 pb-2"><h5 className="text-[10px] font-black text-slate-400 uppercase">Lens Specifications</h5><button type="button" onClick={toggleManualLens} className={`text-[8px] font-black px-2 py-1 rounded border transition-all ${showManualLens ? 'bg-teal-600 text-white border-teal-600' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>Manual Mode</button></div>
                           <div className="space-y-4">
                              <div className="space-y-1"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Lens Type</label>{showManualLens ? <input type="text" name="lens_type" value={formData.dispensing_details.lens_type} onChange={handleDispensingChange} className="edit-input w-full border-teal-200" /> : <select name="lens_type" value={formData.dispensing_details.lens_type} onChange={handleDispensingChange} className="edit-input w-full cursor-pointer"><option value="">Select lens...</option>{inventoryLenses.map(l => <option key={l.id} value={l.item_name}>{l.item_name}</option>)}</select>}</div>
                           </div>
                        </div>
                        <div className="space-y-1 md:col-span-2"><label className="text-[10px] font-black text-slate-400 uppercase">Additional Fitting Remarks</label><textarea name="remarks" value={formData.dispensing_details.remarks} onChange={handleDispensingChange} className="edit-input w-full min-h-[60px]" /></div>
                     </div>
                  </div>
               </div>

               <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-end space-x-3 sticky bottom-0 z-20">
                  <button onClick={() => setEditModal(false)} className="px-10 py-3.5 bg-white text-slate-400 border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50">Cancel</button>
                  <button onClick={handleEditSave} disabled={saving} className={`px-16 py-3.5 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl transition-all flex items-center space-x-3 ${isNewVisit ? 'bg-teal-600 hover:bg-teal-700' : 'bg-slate-900 hover:bg-slate-800'}`}>
                     {saving ? <FiClock className="animate-spin" /> : <FiCheck />}
                     <span>{saving ? 'Processing...' : isNewVisit ? 'Log Visit Record' : 'Apply Corretions'}</span>
                  </button>
               </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"></motion.div>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative bg-white p-10 rounded-xl shadow-2xl w-full max-w-sm text-center space-y-8">
               <div className="w-24 h-24 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto shadow-inner"><FiTrash2 size={40} /></div>
               <div><h4 className="text-2xl font-black text-slate-900 tracking-tight tracking-tight uppercase">Erase Registration?</h4><p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Dossier: {deleteConfirm.reg_no}</p></div>
               <div className="flex flex-col space-y-3"><button onClick={() => handleDelete(deleteConfirm.id)} className="w-full py-4 bg-red-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:bg-red-700 transition-all">Confirm Removal</button><button onClick={() => setDeleteConfirm(null)} className="w-full py-4 bg-slate-100 text-slate-400 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all">Abort Action</button></div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .shadow-top { box-shadow: 0 -4px 6px -1px rgb(0 0 0 / 0.1), 0 -2px 4px -2px rgb(0 0 0 / 0.1); }
        .edit-input {
          background: #f8fafc;
          border: 1.5px solid #f1f5f9;
          border-radius: 1rem;
          padding: 0.75rem 1.25rem;
          font-size: 0.75rem;
          font-weight: 700;
          color: #1e293b;
          outline: none;
          transition: all 0.2s;
        }
        .edit-input:focus {
          border-color: #6366f1;
          background: white;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.05);
        }
      `}} />
    </div>
  );
};

export default Patients;
